import { Auth } from 'aws-amplify'
import config from '~/config'
import {
  createAuthTokenResolver,
  createReportThrottle,
  markTokenUnavailable,
  reportTokenResult,
  reportSessionLost,
  reportSignOut,
  reportUnauthorizedWithoutHeader,
  isTokenUsable,
  stripQuery
} from '~/helpers/axiosAuth'

export default (context, inject) => {
  const { $axios, redirect, store, app } = context
  // @nuxtjs/sentry が注入する $sentry(無効化環境ではモック)
  const sentry = (app && app.$sentry) || context.$sentry
  // 画面のパスはリクエスト時点のものを使う(context.route は画面遷移のたびに更新される。
  // プラグイン初期化時に取り出すと初期画面のパスのまま固定されてしまう)
  const currentFullPath = () => {
    const current =
      context.route || (app && app.router && app.router.currentRoute)
    return current && current.fullPath
  }
  // Sentry に送る画面のパスはクエリを落とす。アプリ内 WebView の /app/* は URL の userId に ID トークンそのものが
  // 入っているため、fullPath をそのまま送るとトークンが Sentry に残る
  const currentPath = () => stripQuery(currentFullPath())
  // 実際に送るヘッダーに X-Authorization があるか(axios は既定ヘッダーを config.headers.common に取り込んでから interceptor を呼ぶ)
  const hasAuthHeader = (request) => {
    const headers = request.headers || {}
    return !!(
      (headers.common && headers.common['X-Authorization']) ||
      headers['X-Authorization']
    )
  }
  // /app/* はアプリから渡されたトークンを store に入れる作りで Amplify のセッションを持たないため、
  // 設計上つねに session-lost になる。切り分け用の通知からは外す
  const isAppWebView = () => /^\/app(\/|$)/.test(currentPath() || '')
  // Sentry 通知の間引き(種類ごとに 1 分に 1 回まで)
  const shouldReport = createReportThrottle()

  // トークン取得(単一飛行 + クールダウン)。middleware(setAccessToken)とも共有する
  const resolveToken = createAuthTokenResolver({
    getToken: async () => {
      const session = await Auth.currentSession()
      return session.getIdToken().getJwtToken()
    }
  })
  // 取得結果(再試行して取れた / 再試行しても取れない)の Sentry 通知(同じ結果からは 1 回だけ)
  const reportResult = (result, { method, url } = {}) =>
    reportTokenResult({
      sentry,
      result,
      shouldReport,
      path: currentPath(),
      method,
      url
    })
  // 直前までトークンがあったのに session-lost になったことの通知。API 呼び出し時(onRequest)と
  // ページ遷移時(middleware/setAccessToken)の両方から使い、同じ取得結果からは 1 回だけ送る
  const reportLost = (result, { method, url, fallback = false } = {}) => {
    if (!result || result.reported) {
      return false
    }
    result.reported = true
    if (isAppWebView() || !shouldReport('session-lost')) {
      return false
    }
    return reportSessionLost({
      sentry,
      error: result.error,
      path: currentPath(),
      method,
      url,
      fallback
    })
  }
  // Auth.signOut() を呼ぶ箇所(401 / middleware / ログアウト操作)で理由付きの通知を送る(切り分け用)。
  // 別タブのサインアウトで localStorage が消えたかどうかを、session-lost 通知と同時刻・同一 IP で突き合わせる
  // hadToken / tokenStatus は呼び出し側(middleware)が自分の store の値を渡せる。省略時はこのプラグインの store を見る
  const reportSignOutReason = (
    reason,
    { method, url, path, hadToken, tokenStatus } = {}
  ) => {
    if (!shouldReport(`sign-out:${reason}`)) {
      return false
    }
    const state = store.state || {}
    return reportSignOut({
      sentry,
      reason,
      path: path || currentPath(),
      method,
      url,
      hadToken: hadToken === undefined ? !!state.accessToken : hadToken,
      tokenStatus:
        tokenStatus === undefined ? state.accessTokenStatus : tokenStatus
    })
  }
  if (typeof inject === 'function') {
    inject('authToken', {
      resolve: resolveToken,
      report: reportResult,
      reportLost,
      reportSignOut: reportSignOutReason
    })
  }

  $axios.onResponseError(async (error) => {
    if (error.response && error.response.status === 401) {
      const request = error.config || {}
      // ヘッダー無しで送った API が 401 を返した = 認証必須の API をヘッダー無しで呼んだ。
      // store にトークンがあったかどうかに関係なく記録する(未ログインで認証必須の API が呼ばれるのも異常なので)
      if (request.authHeader === 'none') {
        shouldReport('unauthorized-no-header') &&
          reportUnauthorizedWithoutHeader({
            sentry,
            tokenStatus: request.tokenStatus,
            hadToken: !!(store.state && store.state.accessToken),
            path: currentPath(),
            method: request.method,
            url: request.url
          })
      }
      // トークンが一時的に取れずにヘッダー無しで送ったリクエストの 401 は、セッション切れではないので
      // サインアウトもリダイレクトもせず、呼び出し元のエラー処理(トースト等)に任せる
      if (request.tokenUnavailable) {
        throw markTokenUnavailable(error)
      }
      reportSignOutReason('response-401', {
        method: request.method,
        url: request.url
      })
      store.commit('setAccessToken', undefined)
      store.commit('setAccessTokenStatus', 'session-lost')
      // ログイン後の戻り先(クエリ込み)。パスが取れないときは保存しない(文字列 "undefined" を保存しないため。ログイン側は既定の遷移先に進む)
      const path = currentFullPath()
      if (path) {
        localStorage.setItem(config.redirectKey, path)
      }
      await Auth.signOut()
      redirect('/login')
      return
    }
    throw error
  })

  $axios.onRequest(async (request) => {
    const result = await resolveToken()
    reportResult(result, { method: request.method, url: request.url })
    // onResponseError で参照する印。tokenStatus は取得結果、authHeader はヘッダーの出どころ
    //   session : Amplify のセッションから取れたトークン
    //   memory  : セッション無しのため store(メモリ)に残っていた期限内のトークン
    //   default : ログイン処理が axios の既定ヘッダー(setHeader)に入れたトークン(取得に失敗してもこれは送られる)
    //   none    : ヘッダー無し
    request.tokenStatus = result.status
    request.authHeader = hasAuthHeader(request) ? 'default' : 'none'

    if (result.status === 'ok') {
      request.headers.common['X-Authorization'] = result.token
      request.authHeader = 'session'
      return request
    }

    if (result.status === 'unavailable') {
      // 一時的にトークンが取れない(リフレッシュ通信の失敗など)。ヘッダー無しで送る(認証不要の API は成功する)が、
      // 認証必須の API が返す 401 でログアウトされないよう印を付けておく(上の onResponseError で見る)
      request.tokenUnavailable = true
    }
    // session-lost: ログインしていない / セッションが無効。ヘッダー無しで送り、BE の 401 を受けて
    // 上の onResponseError でサインアウト → ログイン画面へ(従来どおりの流れ)。
    // ただし直前までトークンがあった(store に accessToken が入っている)のに session-lost になった場合は、
    // 利用者が途中でログアウトされる事象の原因を切り分けるため Sentry に送る(未ログインの通常の呼び出しは送らない)
    if (
      result.status === 'session-lost' &&
      store.state &&
      store.state.accessToken
    ) {
      // ブラウザのサイトデータが消えるなどで Amplify のセッションだけが無くなった場合、
      // store(メモリ)に残っている ID トークンが期限内ならそれで送る。利用者はトークンの期限まで操作を続けられる
      // (本当にログアウト済みなら BE が 401 を返し、上の onResponseError で従来どおりログイン画面へ)
      const fallback = isTokenUsable(store.state.accessToken)
      if (fallback) {
        request.headers.common['X-Authorization'] = store.state.accessToken
        request.tokenFallback = true
        request.authHeader = 'memory'
      }
      reportLost(result, { method: request.method, url: request.url, fallback })
    }
    return request
  })
}
